import { useState, useEffect } from 'react';
import api from '../../api/axios';
import EmptyState, { ClientEmptyIcon } from '../../components/EmptyState';

export default function AdminClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [loginType, setLoginType] = useState('email');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    const res = await api.get('/users');
    setClients(res.data);
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const openAdd = () => {
    setEditClient(null);
    setForm({ name: '', email: '', phone: '', password: '' });
    setLoginType('email');
    setError('');
    setShowModal(true);
  };

  const openEdit = (client) => {
    setEditClient(client);
    const type = client.phone && !client.email ? 'phone' : 'email';
    setLoginType(type);
    setForm({ name: client.name, email: client.email || '', phone: client.phone || '', password: '' });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editClient) {
        await api.put(`/users/${editClient.id}`, form);
      } else {
        await api.post('/users', form);
      }
      await load();
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить клиента? Все его котлы тоже будут удалены.')) return;
    await api.delete(`/users/${id}`);
    setClients((prev) => prev.filter((c) => c.id !== id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Клиенты</h1>
          <p className="text-slate-400 text-sm mt-0.5">{clients.length} пользователей</p>
        </div>
        <button
          onClick={openAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Добавить
        </button>
      </div>

      <div className="space-y-3">
        {clients.length === 0 ? (
          <EmptyState
            icon={<ClientEmptyIcon />}
            title="Нет клиентов"
            description="Нажмите кнопку выше, чтобы добавить первого клиента"
          />
        ) : (
          clients.map((client) => (
            <div key={client.id} className="bg-slate-800 border border-slate-700 rounded-2xl px-4 py-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-white">{client.name}</p>
                <p className="text-sm text-slate-400">{client.email || client.phone}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Зарегистрирован: {new Date(client.created_at).toLocaleDateString('ru-RU')}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openEdit(client)}
                  className="bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg p-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(client.id)}
                  className="bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg p-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-700">
            <h2 className="text-lg font-bold text-white mb-4">
              {editClient ? 'Редактировать клиента' : 'Добавить клиента'}
            </h2>
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-2 mb-4 text-sm">{error}</div>
            )}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Имя</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                  placeholder="Иван Иванов"
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Вход по</label>
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={() => setLoginType('email')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      loginType === 'email' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
                    }`}>Email</button>
                  <button type="button" onClick={() => setLoginType('phone')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      loginType === 'phone' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
                    }`}>Телефон</button>
                </div>
                {loginType === 'email' ? (
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value, phone: '' }))}
                    required
                    placeholder="ivan@example.com"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value, email: '' }))}
                    required
                    placeholder="+7 700 000 0000"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">{editClient ? 'Новый пароль (оставьте пустым)' : 'Пароль'}</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  required={!editClient}
                  placeholder="••••••••"
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setError(''); }}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white rounded-lg py-2.5 text-sm transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-medium transition-colors"
                >
                  {saving ? 'Сохранение...' : editClient ? 'Сохранить' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
